<template>
  <v-container>
    <v-container class="pt-0">
      <!-- Buttons panel for expanded size -->
      <v-col class="text-right hidden-sm-and-down pt-0 pr-0">
        <v-btn
          class="mr-4"
          color="grey darken-1"
          :loading="reloadLoading"
          v-if="savingEnable"
          @click="reload"
          outlined
          tile
        >
          <v-icon left>mdi-autorenew</v-icon>
          {{ $t("reload") }}
        </v-btn>
        <v-btn
          color="primaryLigth"
          :loading="saveLoading"
          :disabled="saveDisabled"
          v-if="savingEnable"
          @click="save"
          outlined
          tile
        >
          <v-icon left>mdi-content-save-outline</v-icon>
          {{ $t("save") }}
        </v-btn>
      </v-col>

      <v-expansion-panels v-model="panels" multiple>
        <!-- General configuration panel -->
        <v-expansion-panel>
          <v-expansion-panel-header>
            {{ $t("General") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-col cols="12" md="6">
              <v-select
                v-model="logsSelect"
                :items="logsList"
                :loading="firstLoading"
                :class="{ 'disable-events': !savingEnable }"
                :label="$t('Logs Level')"
                @change="saveDisabled = false"
                dense
              ></v-select>
            </v-col>
            <v-col cols="12" md="6" class="pb-0">
              <v-select
                v-model="localeSelect"
                :items="localeList"
                :loading="firstLoading"
                :class="{ 'disable-events': !savingEnable }"
                :label="$t('Language')"
                @change="saveDisabled = false"
                dense
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="tzSelect"
                :items="tzList"
                :search-input.sync="search"
                :class="{ 'disable-events': !savingEnable }"
                @change="saveDisabled = false"
                cache-items
                flat
                hide-no-data
                hide-details
                :label="$t('Timezone')"
              ></v-autocomplete>
            </v-col>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- DICOM Index configuration panel -->
        <v-expansion-panel>
          <v-expansion-panel-header>
            {{ $t("DICOM Index") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-col cols="12" md="6" class="pt-0 pb-6">
              <v-sheet :color="pageBackground" elevation="1" rounded>
                <v-container>
                  <v-row>
                    <v-col>
                      <v-switch
                        v-model="postgresEnabled"
                        :loading="firstLoading"
                        :class="{ 'disable-events': !savingEnable }"
                        @change="saveDisabled = false"
                        :label="$t('PostgreSQL Enabled')"
                        dense
                      ></v-switch>
                    </v-col>
                    <v-col style="text-align: right;" class="my-auto pr-5">
                      <v-btn
                        :color="
                          testPostgresStatus == 'unknown'
                            ? 'grey darken-1'
                            : testPostgresStatus == 'error'
                            ? 'error'
                            : 'success'
                        "
                        :loading="testPostgresLoading"
                        @click="testPostgres"
                        outlined
                        tile
                      >
                        <v-icon left v-if="testPostgresStatus == 'unknown'">mdi-database-search</v-icon>
                        <v-icon left v-if="testPostgresStatus == 'error'">mdi-database-remove</v-icon>
                        <v-icon left v-if="testPostgresStatus == 'success'">mdi-database-check</v-icon>
                        {{ $t("test") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-text-field
                        v-model="postgresIp"
                        :loading="firstLoading"
                        :class="{ 'disable-events': !savingEnable }"
                        @input="
                          saveDisabled = false;
                          testPostgresStatus = 'unknown';
                        "
                        :label="$t('PostgreSQL IP')"
                        dense
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-text-field
                        v-model="postgresPort"
                        :loading="firstLoading"
                        :class="{ 'disable-events': !savingEnable }"
                        @input="
                          saveDisabled = false;
                          testPostgresStatus = 'unknown';
                        "
                        :label="$t('PostgreSQL Port')"
                        type="number"
                        dense
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-text-field
                        v-model="postgresUsername"
                        :loading="firstLoading"
                        :class="{ 'disable-events': !savingEnable }"
                        @input="
                          saveDisabled = false;
                          testPostgresStatus = 'unknown';
                        "
                        :label="$t('PostgreSQL Username')"
                        dense
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-text-field
                        v-model="postgresPassword"
                        :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                        :type="showPassword ? 'text' : 'password'"
                        :loading="firstLoading"
                        :class="{ 'disable-events': !savingEnable }"
                        @input="
                          saveDisabled = false;
                          testPostgresStatus = 'unknown';
                        "
                        @click:append="showPassword = !showPassword"
                        :label="$t('PostgreSQL Password')"
                        dense
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-text-field
                        v-model="postgresDatabase"
                        :loading="firstLoading"
                        :class="{ 'disable-events': !savingEnable }"
                        @input="
                          saveDisabled = false;
                          testPostgresStatus = 'unknown';
                        "
                        :label="$t('PostgreSQL Database')"
                        dense
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <h4 class="text-right">
                        {{ $t("Service restart required.") }}
                      </h4>
                    </v-col>
                  </v-row>
                </v-container>
              </v-sheet>
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="dicomIndexSelect"
                :items="dicomIndexList"
                :loading="firstLoading"
                :class="{ 'disable-events': !savingEnable || disableIndexSelection }"
                :label="$t('Selected Database')"
                :messages="[$t('Require PostgreSQL enabled and tested.')]"
                @change="saveDisabled = false"
                dense
              ></v-select>
            </v-col>

          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <!-- Buttons panel for shrinked size -->
    <v-container class="text-right hidden-md-and-up">
      <v-btn
        class="mr-4"
        color="grey darken-1"
        :loading="reloadLoading"
        @click="reload"
        outlined
        tile
      >
        <v-icon left>mdi-autorenew</v-icon>
        {{ $t("reload") }}
      </v-btn>
      <v-btn
        color="primaryLigth"
        :loading="saveLoading"
        :disabled="saveDisabled"
        @click="save"
        outlined
        tile
      >
        <v-icon left>mdi-content-save-outline</v-icon>
        {{ $t("save") }}
      </v-btn>
    </v-container>
  </v-container>
</template>

<script>
import axios from "axios";
import i18n from "../../i18n";
import EventBus from "../../event-bus";

export default {
  data: () => ({
    // log
    logsSelect: "",
    logsList: [
      {
        value: "Info",
        text: i18n.t("Info"),
      },
      {
        value: "Debug",
        text: i18n.t("Debug"),
      },
      {
        value: "Error",
        text: i18n.t("Error"),
      },
    ],
    // Language
    localeList: [],
    localeSelect: "",
    // Timezone
    tzList: [],
    tzSelect: null,
    // PostreSQL
    showPassword: false,
    postgresEnabled: false,
    postgresIp: "",
    postgresPort: 0,
    postgresUsername: "",
    postgresPassword: "",
    postgresDatabase: "",
    // DICOM index write
    dicomIndexSelect: "",
    dicomIndexList: ["MongoDB", "PostgreSQL"],
    // Test postgres
    testPostgresLoading: false,
    testPostgresStatus: "unknown", // unknown, successful, error
    // Control
    search: null,
    saveLoading: false,
    saveDisabled: true,
    reloadLoading: false,
    firstLoading: true,
    savingEnable: false,
    panels: [0, 1],
    disableIndexSelection: true,
  }),
  mounted() {
    // Permissions
    this.savingEnable = this.$store.getters.resources.configuration.main.update;
    this.loadData();
  },
  computed: {
    pageBackground() {
      if (this.$vuetify.theme.dark) {
        return "grey darken-2";
      } else {
        return "grey lighten-4";
      }
    },
  },
  watch: {
    search(val) {
      val && val !== this.select && this.timezonesQuerySelections(val);
    },
  },
  methods: {
    timezonesQuerySelections(v) {
      this.tzList = this.$tzList.filter((e) => {
        return (e || "").toLowerCase().indexOf((v || "").toLowerCase()) > -1;
      });
    },
    sendError(error) {
      if (error.response) {
        if (
          error.response.status == 500 &&
          error.response.data.status == "error"
        ) {
          EventBus.$emit("SHOW_ERROR", error.response.data.error);
        } else {
          EventBus.$emit("SHOW_ERROR", error.response.data);
        }
      } else {
        EventBus.$emit("SHOW_ERROR", error);
      }
    },
    loadData() {
      axios({
        url: "/api/v1/config/main",
        method: "GET",
      })
        .then((response) => {
          // Log
          this.logsSelect = response.data.main.logsLevel;
          // Language
          let localeList = JSON.parse(JSON.stringify(this.$localeList));
          localeList.forEach((item) => {
            item.text = i18n.t(item.text);
          });
          this.localeList = localeList;
          this.localeSelect = response.data.main.locale;
          // Timezone
          let timezone = response.data.main.timezone.replace("_", " ");
          this.tzList = this.$tzList;
          this.tzSelect = timezone;
          // PostgreSQL
          this.postgresEnabled = Boolean(response.data.main.postgresEnabled);
          this.postgresIp = response.data.main.postgresIp;
          this.postgresPort = response.data.main.postgresPort;
          this.postgresUsername = response.data.main.postgresUsername;
          this.postgresPassword = response.data.main.postgresPassword;
          this.postgresDatabase = response.data.main.postgresDatabase;
          this.dicomIndexSelect = response.data.main.dicomIndex;
          // Enable if PostgreSQL selected as index
          if (this.dicomIndexSelect == "PostgreSQL") {
            this.disableIndexSelection = false;
          }
          // Control
          this.reloadLoading = false;
          this.firstLoading = false;
          this.saveDisabled = true;
        })
        .catch((error) => {
          this.reloadLoading = false;
          this.firstLoading = false;
          this.sendError(error);
        });
    },
    reload() {
      this.reloadLoading = true;
      this.testPostgresStatus = "unknown";
      this.loadData();
    },
    save() {
      this.saveLoading = true;

      // Send configuration
      axios({
        url: "/api/v1/config/main",
        method: "PUT",
        data: {
          timezone: this.tzSelect.replace(" ", "_"),
          locale: this.localeSelect,
          logsLevel: this.logsSelect,
          postgresEnabled: this.postgresEnabled,
          postgresIp: this.postgresIp,
          postgresPort: Number(this.postgresPort),
          postgresUsername: this.postgresUsername,
          postgresPassword: this.postgresPassword,
          postgresDatabase: this.postgresDatabase,
          dicomIndex: this.dicomIndexSelect,
        },
      })
        .then((response) => {
          this.saveLoading = false;

          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            this.sendError(response.data.error);
          } else {
            this.saveDisabled = true;
          }
        })
        .catch((error) => {
          this.saveLoading = false;
          this.sendError(error);
        });
    },
    checkNested(obj, level, ...rest) {
      if (obj === undefined) return false;
      if (rest.length == 0 && {}.hasOwnProperty.call(obj, level)) return true;
      return this.checkNested(obj[level], ...rest);
    },
    testPostgres() {
      this.testPostgresLoading = true;

      axios({
        url: "/api/v1/config/test/postgres",
        method: "POST",
        data: {
          logsLevel: "Info",
          locale: "",
          timezone: "",
          postgresEnabled: true,
          postgresIp: this.postgresIp,
          postgresPort: Number(this.postgresPort),
          postgresUsername: this.postgresUsername,
          postgresPassword: this.postgresPassword,
          postgresDatabase: this.postgresDatabase,
          dicomIndex: "PostgreSQL",
        },
      })
        .then((response) => {
          this.testPostgresLoading = false;

          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            this.testPostgresStatus = "error";
            console.error("POSTGRESQL:", response.data.message);
          } else {
            this.testPostgresStatus = "success";
            if (this.postgresEnabled) {
              this.disableIndexSelection = false;
            }
          }
        })
        .catch((error) => {
          this.testPostgresLoading = false;
          this.testPostgresStatus = "error";
          this.disableIndexSelection = true;
          this.sendError(error);
        });
    },
  },
};
</script>

<style scoped>
.disable-events {
  pointer-events: none;
}
</style>